import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

type Tone = "info" | "warn" | "ok" | "error";

const TONES: Record<Tone, { box: string; icon: string; glyph: string }> = {
  info: { box: "bg-teal-wash text-ink", icon: "bg-teal text-white", glyph: "i" },
  warn: { box: "bg-warn-wash text-ink", icon: "bg-warn text-white", glyph: "!" },
  ok: { box: "bg-ok-wash text-ink", icon: "bg-ok text-white", glyph: "✓" },
  error: { box: "bg-red-50 text-ink", icon: "bg-danger text-white", glyph: "!" },
};

interface NoteProps {
  tone?: Tone;
  className?: string;
  children: ReactNode;
}

export function Note({ tone = "info", className, children }: NoteProps) {
  const style = TONES[tone];
  return (
    <div
      // Errors and warnings are announced; informational notes are not, so a
      // screen reader is not interrupted by static explanatory copy.
      role={tone === "error" || tone === "warn" ? "alert" : undefined}
      className={cn(
        "flex items-start gap-2.5 rounded-[9px] px-3.5 py-3 text-[13.5px]",
        style.box,
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-px grid size-[18px] shrink-0 place-items-center rounded-full text-[11px] font-bold",
          style.icon,
        )}
      >
        {style.glyph}
      </span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
